import { Box, Button, HStack, Image, Text, Tooltip } from '@chakra-ui/react';
import { Icon } from '@blueprintjs/core';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import MapContext from '../../context/MapContext';
import UIContext from '../../context/UIContext';
import { REPORT_CATEGORY_BY_ID } from '../../utils/userReports';

export default function UserReportPopup({ popup, onClose, onVoted, onDeleted }) {
    const { mapRef } = useContext(MapContext);
    const { voteOnUserReport, deleteUserReport } = useContext(UIContext);
    const containerRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [anchor, setAnchor] = useState(null);
    const [upvoteCount, setUpvoteCount] = useState(0);
    const [downvoteCount, setDownvoteCount] = useState(0);
    const [userVote, setUserVote] = useState(null);
    const [voting, setVoting] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!popup) {
            setUpvoteCount(0);
            setDownvoteCount(0);
            setUserVote(null);
            return;
        }
        setUpvoteCount(popup.upvoteCount || 0);
        setDownvoteCount(popup.downvoteCount || 0);
    }, [popup?.id, popup?.upvoteCount, popup?.downvoteCount]);

    useEffect(() => {
        setUserVote(null);
    }, [popup?.id]);

    useEffect(() => {
        if (!popup || !mapRef.current) {
            setAnchor(null);
            setPosition(null);
            return;
        }

        const map = mapRef.current;

        const updateAnchor = () => {
            if (!mapRef.current) {
                setAnchor(null);
                return;
            }
            const point = mapRef.current.project([popup.longitude, popup.latitude]);
            setAnchor({ x: point.x, y: point.y });
        };

        updateAnchor();
        map.on('move', updateAnchor);
        map.on('zoom', updateAnchor);
        map.on('rotate', updateAnchor);
        map.on('pitch', updateAnchor);
        map.on('resize', updateAnchor);

        return () => {
            map.off('move', updateAnchor);
            map.off('zoom', updateAnchor);
            map.off('rotate', updateAnchor);
            map.off('pitch', updateAnchor);
            map.off('resize', updateAnchor);
        };
    }, [popup, mapRef]);

    useLayoutEffect(() => {
        if (!popup || !anchor || !mapRef.current || !containerRef.current) {
            setPosition(null);
            return;
        }

        const mapEl = mapRef.current.getContainer();
        const popupEl = containerRef.current;
        const mapW = mapEl.clientWidth;
        const mapH = mapEl.clientHeight;
        const popupW = popupEl.offsetWidth;
        const popupH = popupEl.offsetHeight;

        // Prefer above the marker, centered; fall below if needed.
        let left = anchor.x - popupW / 2;
        let top = anchor.y - popupH - 14;
        if (top < 10) {
            top = anchor.y + 14;
        }

        left = Math.min(Math.max(left, 10), Math.max(10, mapW - popupW - 10));
        top = Math.min(Math.max(top, 10), Math.max(10, mapH - popupH - 10));

        setPosition({ left, top });
    }, [popup, anchor, mapRef, upvoteCount, downvoteCount, userVote, popup?.description, deleting]);

    const handleVote = async (vote) => {
        if (!popup?.id || voting || deleting) {
            return;
        }
        setVoting(true);
        try {
            const updated = await voteOnUserReport(popup.id, { vote });
            setUpvoteCount(updated.upvoteCount || 0);
            setDownvoteCount(updated.downvoteCount || 0);
            setUserVote(vote);
            onVoted?.(updated);
        } catch (e) {
            console.log('Failed to vote on user report', e);
        } finally {
            setVoting(false);
        }
    };

    const handleDelete = async () => {
        if (!popup?.id || deleting || voting) {
            return;
        }
        setDeleting(true);
        try {
            await deleteUserReport(popup.id);
            onDeleted?.(popup.id);
            onClose?.();
        } catch (e) {
            console.log('Failed to delete user report', e);
        } finally {
            setDeleting(false);
        }
    };

    if (!popup || !anchor) {
        return null;
    }

    const iconSrc = popup.iconSrc
        || REPORT_CATEGORY_BY_ID[popup.reportType]?.iconSrc
        || REPORT_CATEGORY_BY_ID.hazard.iconSrc;
    const totalVotes = (upvoteCount || 0) - (downvoteCount || 0);
    const showFooter = popup.reportedAt || popup.id;
    const isOwner = !!popup.isOwner;

    return (
        <Box
            ref={containerRef}
            pointerEvents="auto"
            zIndex={21}
            position="absolute"
            left={position ? `${position.left}px` : `${anchor.x}px`}
            top={position ? `${position.top}px` : `${anchor.y}px`}
            transform={position ? undefined : 'translate(-50%, calc(-100% - 14px))'}
            visibility={position ? 'visible' : 'hidden'}
            display="flex"
            flexDir="column"
            justifyContent="stretch"
            alignItems="stretch"
            bg="rgba(255,255,255,0.9)"
            borderRadius="4px"
            border="1px solid rgba(0,0,0,0.1)"
            shadow="md"
            padding="0px"
            overflow="hidden"
            boxShadow="0px 0px 0px 2px rgba(0,0,0,0.1)"
            minW="200px"
            userSelect="none"
        >
            <HStack spacing={2} p="10px" pr="44px" align="center">
                <Image
                    src={iconSrc}
                    alt=""
                    boxSize="18px"
                    opacity={0.5}
                    flexShrink={0}
                />
                <Text fontWeight="500">{popup.typeLabel}</Text>
            </HStack>
            {showFooter && (
                <HStack
                    spacing={1}
                    bg="rgba(0,0,0,0.05)"
                    w="100%"
                    h="40px"
                    minH="40px"
                    borderTop="1px solid rgba(0,0,0,0.1)"
                    pl="8px"
                    pr="0"
                    py="0"
                    align="center"
                    justify="space-between"
                >
                    <Text fontSize="13px" flex="1" minW={0} noOfLines={1}>
                        {popup.reportedAt ? `Reported ${popup.reportedAt}` : ''}
                    </Text>
                    {popup.id && (
                        <HStack spacing={0} flexShrink={0}>
                            <Button
                                variant="ghost"
                                isDisabled={voting || deleting}
                                onClick={() => handleVote('valid')}
                                minW="40px"
                                w="40px"
                                h="40px"
                                p="0"
                                borderRadius="0"
                                bg={userVote === 'valid' ? 'rgba(56, 161, 105, 0.18)' : 'transparent'}
                                color={userVote === 'valid' ? 'green.700' : 'rgba(0,0,0,0.75)'}
                                _hover={{
                                    bg: userVote === 'valid' ? 'rgba(56, 161, 105, 0.28)' : 'rgba(0,0,0,0.06)',
                                }}
                            >
                                <Icon icon="thumbs-up" size={16} />
                            </Button>
                            <Button
                                variant="ghost"
                                isDisabled={voting || deleting}
                                onClick={() => handleVote('invalid')}
                                minW="40px"
                                w="40px"
                                h="40px"
                                p="0"
                                borderRadius="0"
                                bg={userVote === 'invalid' ? 'rgba(229, 62, 62, 0.18)' : 'transparent'}
                                color={userVote === 'invalid' ? 'red.700' : 'rgba(0,0,0,0.75)'}
                                _hover={{
                                    bg: userVote === 'invalid' ? 'rgba(229, 62, 62, 0.28)' : 'rgba(0,0,0,0.06)',
                                }}
                            >
                                <Icon icon="thumbs-down" size={16} />
                            </Button>
                            <Box
                                px="6px"
                                py="1px"
                                borderRadius="4px"
                                bg="rgba(0,0,0,0.08)"
                                border="1px solid rgba(0,0,0,0.1)"
                            >
                                <Text fontSize="12px" fontWeight="600" mb="0px" lineHeight="18px">
                                    {totalVotes}
                                </Text>
                            </Box>
                            {isOwner && (
                                <Button
                                    variant="ghost"
                                    isDisabled={deleting || voting}
                                    onClick={handleDelete}
                                    minW="40px"
                                    w="40px"
                                    h="40px"
                                    ml="4px"
                                    mr="0"
                                    p="0"
                                    borderRadius="0"
                                    color="red.500"
                                    _hover={{
                                        bg: 'rgba(229, 62, 62, 0.12)',
                                        color: 'red.600',
                                    }}
                                >
                                    <Icon icon="trash" size={16} />
                                </Button>
                            )}
                        </HStack>
                    )}
                </HStack>
            )}
            {popup.description ? (
                <Box
                    w="100%"
                    bg="rgba(0,0,0,0.05)"
                    borderTop="1px solid rgba(0,0,0,0.1)"
                    px="10px"
                    py="8px"
                >
                    <Text
                        fontSize="13px"
                        fontStyle="italic"
                        color="rgba(0,0,0,0.75)"
                        maxW="200px"
                        whiteSpace="normal"
                        wordBreak="break-word"
                    >
                        {popup.description}
                    </Text>
                </Box>
            ) : null}
            <Tooltip placement="right" label={<Text>Close</Text>}>
                <Button
                    _hover={{
                        color: 'rgba(0,0,0,1.0)',
                    }}
                    bg={''}
                    w="40px"
                    h="40px"
                    position="absolute"
                    top="0px"
                    right="0px"
                    onClick={onClose}
                    color="rgba(0,0,0,0.8)"
                >
                    <Text mb="0px">
                        <Icon icon="cross" size="16" />
                    </Text>
                </Button>
            </Tooltip>
        </Box>
    );
}
